import React from "react";
import "./Landingpage.css";
import Container from "react-bootstrap/Container";
import Nav from "react-bootstrap/Nav";
import Navbar from "react-bootstrap/Navbar";
import logo from "../../images/urology-logo.png";
import logowhite from "../../images/urology-logo-transparent.png";
import dotsbg from "../../images/Background-dots.svg";
import checksolid from "../../images/circle-check-solid.svg";
import abdm from "../../images/abdm-logo.svg";
import gdpr from "../../images/gdpr_logo.svg";
import hipaa from "../../images/hipaa-logo.svg";
import iconquotestart from "../../images/Icon-quote-start.svg";
import iconquoteend from "../../images/Icon-quote-end.svg";
import testimonialbg from "../../images/testimonial-bg.svg";
import bgcircles from "../../images/bg-circles.svg";
import globe from "../../images/globe.svg";
import homeframe from "../../images/home-frame.svg";
import fbicon from "../../images/fb-icon.svg";
import linkedinicon from "../../images/linkedin-icon.svg";
import twittericon from "../../images/social-twitter.svg";
import instagramicon from "../../images/instagram-icon.svg";
import Button from "react-bootstrap/Button";
import { Col, Row } from "react-bootstrap";
import Form from "react-bootstrap/Form";
import { Link } from "react-router-dom";
import herodashboard from "../../images/hero-dashboard.svg";
import apppatient from "../../images/app-patient.svg";
import appprovider from "../../images/app-provider.svg";
import { toast } from "react-toastify";
import { FaApple, FaGooglePlay } from "react-icons/fa";
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { connectWithUs } from "../../services/authservice";
import {
  useRevealOnScroll,
  useStickyHeader,
  useParallax,
  useSmoothScroll,
  scrollToSection,
  CountUp,
} from "./animations";

/* The three numbers in the navy band. Written here rather than inline so the
   band stays one shape no matter how many figures the business wants to show. */
const STATS = [
  { value: "300+", label: "Doctors" },
  { value: "100,000+", label: "Patients" },
  { value: "30+", label: "Countries" },
];

/* The certifications the platform holds. `mark` is the wordmark inside the SVG,
   so the alt text names the certification rather than repeating "logo". */
const COMPLIANCE = [
  { src: abdm, alt: "Ayushman Bharat Digital Mission compliant" },
  { src: gdpr, alt: "GDPR compliant" },
  { src: hipaa, alt: "HIPAA compliant" },
];

/* What the two product columns list. Kept as data so the two blocks are one
   component with one set of styles instead of two hand-repeated lists. */
const PATIENT_FEATURES = [
  "Personalized Educational Modules",
  "Medication Reminders",
  "Symptom Tracking & Analytics",
  "Supportive Community",
  "Secure in-app messaging with Providers",
  "Personal AI Health Assistant",
];

const PROVIDER_FEATURES = [
  "User-friendly patient portal",
  "Patient Level Analytics",
  "Population Data Insights",
  "Collaborative Case Sharing",
  "Embedded Clinical Content",
  "Integrated research tools",
  "Clinical Trial Opportunities",
];

/* TODO: real, attributable customer quotes go here, e.g.
     { quote: "...", name: "Dr. ...", role: "...", photo: <imported asset> }
   The section renders itself only once this list has entries, so the page never
   ships invented endorsements. */
const TESTIMONIALS = [];

/* Footer columns. Every resource entry points at a route that exists in
   `src/public/routes/public.js`. */
const PLATFORM_LINKS = [
  "Patient mobile app",
  "Provider workspace",
  "Population analytics",
  "Clinical trial matching",
];

const RESOURCE_LINKS = [
  { label: "Privacy policy", to: "/privacy-policy" },
  { label: "Terms and conditions", to: "/terms-conditions" },
  { label: "Delete your account", to: "/delete-account-step" },
];

const APP_LINKS = [
  {
    label: "Google Play",
    eyebrow: "GET IT ON",
    href: "https://play.google.com/store/apps/details?id=com.corepat.mobile.app&pcampaignid=web_share",
    icon: FaGooglePlay,
  },
  {
    label: "App Store",
    eyebrow: "Download on the",
    href: "https://apps.apple.com/in/app/corepath/id6744514794",
    icon: FaApple,
  },
];

/* One list, one markup path, for both the patient and the provider column. */
function FeatureList({ title, items }) {
  return (
    <div className="feature-list-block">
      <h3>{title}</h3>
      <ul className="stagger-list">
        {items.map((item) => (
          <li key={item}>
            <img src={checksolid} alt="" aria-hidden="true" />
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

function LandingpgComponent() {
  useRevealOnScroll();
  useSmoothScroll();
  const headerStuck = useStickyHeader();
  const heroPosterRef = useParallax();

  const formik = useFormik({
    initialValues: {
      fullName: '',
      phoneNumber: '',
      email: '',
      message: '',
    },
    validationSchema: Yup.object({
      fullName: Yup.string()
        .min(2, 'Name must be at least 2 characters')
        .required('Full name is required'),
      phoneNumber: Yup.string()
        .matches(/^[0-9]{10}$/, 'Phone number must be 10 digits')
        .required('Phone number is required'),
      email: Yup.string()
        .email('Invalid email address')
        .required('Email is required'),
      message: Yup.string()
        .min(10, 'Message must be at least 10 characters')
        .required('Message is required'),
    }),

    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        const response = await connectWithUs({
          fullName: values.fullName,
          phoneNumber: values.phoneNumber,
          email: values.email,
          message: values.message,
        });

        if (response.code === 200) {
          toast.success('Thanks. Your message has been sent.');
          resetForm();
        } else {
          toast.error('Submission failed! Please try again.');
        }
      } catch (error) {
        toast.error('Submission failed! Please try again.');
      } finally {
        setSubmitting(false);
      }
    },
  });

  /* Bootstrap's `isInvalid` wants a boolean. `touched && errors` hands it the
     message string, which React then warns about on every keystroke. */
  const invalid = (field) =>
    Boolean(formik.touched[field] && formik.errors[field]);

  return (
    <div className="landingpgbody">
      <header className={headerStuck ? "is-stuck" : ""}>
        <Navbar collapseOnSelect expand="lg" className="bg-body-tertiary">
          <Container>
            <Navbar.Brand
              as="button"
              type="button"
              aria-label="Back to top"
              onClick={() => scrollToSection()}
            >
              <img src={logo} alt="Rainlink" />
            </Navbar.Brand>
            <Navbar.Toggle aria-controls="responsive-navbar-nav" />
            <Navbar.Collapse id="responsive-navbar-nav">
              <Nav className="ms-auto nav-left align-items-lg-center">
                <Nav.Link
                  as="button"
                  type="button"
                  className="navlink-text"
                  onClick={() => scrollToSection("platform")}
                >
                  Platform
                </Nav.Link>
                <Nav.Link
                  as="button"
                  type="button"
                  className="navlink-text"
                  onClick={() => scrollToSection("compliance")}
                >
                  Compliance
                </Nav.Link>
                <Nav.Link
                  as="button"
                  type="button"
                  className="navlink-text"
                  onClick={() => scrollToSection("contact")}
                >
                  Contact
                </Nav.Link>
                <Button
                  as={Link}
                  to="/signin"
                  className="border-btn signup-btn"
                  variant="outline-primary"
                >
                  Sign In
                </Button>
              </Nav>
            </Navbar.Collapse>
          </Container>
        </Navbar>
      </header>

      <section className="hero-section">
        <Container>
          <Row className="home-blk align-items-center">
            <Col lg={7} className="homepg-left">
              <h1 className="hero-in hero-in-1">
                Building the Infrastructure <br />
                for <span>Pelvic Health</span>
              </h1>
              <p className="sub-title hero-in hero-in-2">
                An AI-enabled platform that helps providers and patients improve
                pelvic health outcomes with personalized insights and predictive
                analytics.
              </p>
              <Button
                className="border-btn explore-btn hero-in hero-in-3"
                variant="outline-primary"
                type="button"
                onClick={() => scrollToSection("platform")}
              >
                Explore our platform
              </Button>
            </Col>
            <Col lg={5} className="homepg-right">
              <div className="home-poster" ref={heroPosterRef}>
                <div className="frame">
                  <img src={homeframe} alt="" aria-hidden="true" />
                  <div className="poster">
                    <img
                      src={herodashboard}
                      alt="Pelvic Health Analytics dashboard"
                    />
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="banner-blk">
        <div className="dot-top">
          <img src={dotsbg} alt="" aria-hidden="true" />
        </div>
        <div className="dot-bottom">
          <img src={dotsbg} alt="" aria-hidden="true" />
        </div>
        <div className="globe-bg">
          <img src={globe} alt="" aria-hidden="true" />
        </div>
        <div className="inner-banner-blk">
          <Container>
            <Row className="industry-blk">
              <Col lg={7}>
                <h2 data-reveal="up">
                  The Industry’s Largest <br />
                  <span>Global Pelvic Health RWD</span>
                </h2>
                <div className="stat-row" data-reveal="up">
                  {STATS.map((stat) => (
                    <div className="stat-item" key={stat.label}>
                      <div className="stat-value">
                        <CountUp value={stat.value} />
                      </div>
                      <p className="stat-label">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </Col>
              <Col lg={5}></Col>
            </Row>
          </Container>
        </div>
      </section>

      <section className="patient-provider-blk" id="platform">
        <div className="dotbg">
          <img src={dotsbg} alt="" aria-hidden="true" />
        </div>
        <Container>
          <div className="section-para" data-reveal="blur">
            <p>
              With <span className="bold">instinctive workflows,</span>{" "}
              evidence-based treatment guidelines, and{" "}
              <span className="bold">actionable data,</span> we help urologists
              and gynecologists improve patient outcomes, streamline operations,
              and grow revenue.
            </p>
          </div>

          <Row className="patient-section align-items-center">
            <Col
              lg={6}
              className="upload-patient d-flex justify-content-center"
              data-reveal="left"
            >
              <div className="frame-patient">
                <img src={bgcircles} alt="" aria-hidden="true" />
                <div className="frame-poster-patient">
                  <img src={apppatient} alt="Patient mobile app" />
                </div>
              </div>
            </Col>
            <Col lg={6} className="patient-blk" data-reveal="right">
              <FeatureList title="Patients" items={PATIENT_FEATURES} />
            </Col>
          </Row>

          <Row className="provider-section align-items-center">
            <Col
              lg={6}
              className="patient-blk order-2 order-lg-1"
              data-reveal="left"
            >
              <FeatureList title="Providers" items={PROVIDER_FEATURES} />
            </Col>
            <Col
              lg={6}
              className="upload-provider d-flex justify-content-center order-1 order-lg-2"
              data-reveal="right"
            >
              <div className="frame-provider">
                <img src={bgcircles} alt="" aria-hidden="true" />
                <div className="frame-poster-provider">
                  <img src={appprovider} alt="Provider clinical workspace" />
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="advanced-section">
        <Container>
          <div className="advanced-blk" data-reveal="zoom">
            <div className="advanced-content">
              <h2>Advanced Data and AI for Pelvic Health</h2>
              <p>
                We turn real-world pelvic health data into decisions at the point
                of care, working with clinicians, medical societies, and
                biomedical innovators to keep clinical pathways current.
              </p>
            </div>
          </div>
        </Container>

        <div className="regularity-blk" id="compliance">
          <Container>
            <div className="safety-compliance-block">
              <h2 className="regulatory-text" data-reveal="left">
                Regulatory, Safety &amp; Compliance
              </h2>
              <div className="compliance-logos" data-reveal="up">
                {COMPLIANCE.map((item) => (
                  <div className="logo-blk" key={item.alt}>
                    <img src={item.src} alt={item.alt} />
                  </div>
                ))}
              </div>
            </div>
          </Container>
        </div>
      </section>

      {TESTIMONIALS.length > 0 && (
        <section className="Testimonials-blk">
          <div className="testimonial-bg">
            <img src={testimonialbg} alt="" aria-hidden="true" />
          </div>
          <Container>
            <h2 data-reveal="up">Testimonials</h2>
            <div className="client-info-blk">
              {TESTIMONIALS.map((item, index) => (
                <figure
                  className={`client-blk rv-d${index + 1}`}
                  data-reveal="up"
                  key={item.name}
                >
                  <div className="qoute-icon-top">
                    <img src={iconquotestart} alt="" aria-hidden="true" />
                  </div>
                  <div className="qoute-icon-bottom">
                    <img src={iconquoteend} alt="" aria-hidden="true" />
                  </div>
                  <div className="client-info d-flex align-items-center">
                    <div className="client-img">
                      <img src={item.photo} alt="" aria-hidden="true" />
                    </div>
                    <figcaption className="client-name">
                      <p className="m-0">{item.name}</p>
                      <p className="client-role m-0">{item.role}</p>
                    </figcaption>
                  </div>
                  <blockquote className="client-description">
                    <p className="m-0">{item.quote}</p>
                  </blockquote>
                </figure>
              ))}
            </div>
          </Container>
        </section>
      )}

      <section className="contactus-blk" id="contact">
        <Container>
          <div className="contactus-bg" data-reveal="zoom">
            <Row className="contactus-row align-items-center">
              <Col lg={6} className="contactus-intro">
                <h2>Connect with us</h2>
                <p>
                  See how the platform can help you drive better patient
                  outcomes and put real-world data to work.
                </p>
                <p className="contactus-signin">
                  Already have an account? <Link to="/signin">Sign In</Link>
                </p>
              </Col>

              <Col lg={6} className="contactus-main-col">
                <div className="contactus-main">
                  <Form onSubmit={formik.handleSubmit} noValidate>
                    <Form.Group className="field-group" controlId="formFullName">
                      <Form.Label>Full name</Form.Label>
                      <Form.Control
                        type="text"
                        name="fullName"
                        autoComplete="name"
                        className="form-inputs"
                        value={formik.values.fullName}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        isInvalid={invalid("fullName")}
                      />
                      <Form.Control.Feedback type="invalid">
                        {formik.errors.fullName}
                      </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="field-group" controlId="formPhoneNumber">
                      <Form.Label>Phone number</Form.Label>
                      <Form.Control
                        type="tel"
                        name="phoneNumber"
                        inputMode="numeric"
                        autoComplete="tel"
                        className="form-inputs"
                        value={formik.values.phoneNumber}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        isInvalid={invalid("phoneNumber")}
                      />
                      <Form.Control.Feedback type="invalid">
                        {formik.errors.phoneNumber}
                      </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="field-group" controlId="formEmail">
                      <Form.Label>Email</Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        autoComplete="email"
                        className="form-inputs"
                        value={formik.values.email}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        isInvalid={invalid("email")}
                      />
                      <Form.Control.Feedback type="invalid">
                        {formik.errors.email}
                      </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="field-group" controlId="formMessage">
                      <Form.Label>Message</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={4}
                        name="message"
                        className="form-inputs msg-input"
                        value={formik.values.message}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        isInvalid={invalid("message")}
                      />
                      <Form.Control.Feedback type="invalid">
                        {formik.errors.message}
                      </Form.Control.Feedback>
                    </Form.Group>

                    <Button
                      className="border-btn btn-booknow"
                      variant="outline-primary"
                      type="submit"
                      disabled={formik.isSubmitting}
                    >
                      {formik.isSubmitting ? 'Sending...' : 'Request a demo'}
                    </Button>
                  </Form>
                </div>
              </Col>
            </Row>
          </div>
        </Container>
      </section>

      <footer className="footer-section">
        <Container>
          <div className="footer-blk">
            <Row>
              <Col md={5} className="Urology-info-blk" data-reveal="up">
                <div className="footer-logo">
                  <img src={logowhite} alt="Rainlink" />
                </div>
                <div className="footer-text">
                  <p>
                    A pelvic health platform connecting urology and gynecology
                    teams with the patients they treat.
                  </p>
                </div>
                <div className="footer-apps">
                  <p>Download the app</p>
                  <div className="footer-app-links">
                    {APP_LINKS.map(({ label, eyebrow, href, icon: StoreIcon }) => (
                      <a
                        key={label}
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`Download CorePath on ${label}`}
                      >
                        <StoreIcon aria-hidden="true" />
                        <span>
                          <small>{eyebrow}</small>
                          <strong>{label}</strong>
                        </span>
                      </a>
                    ))}
                  </div>
                </div>
                {/* TODO: swap these for real profile URLs and they become links. */}
                <div className="social-icons d-flex">
                  <span>
                    <img src={fbicon} className="fb" alt="" aria-hidden="true" />
                  </span>
                  <span>
                    <img src={linkedinicon} className="linkedIn" alt="" aria-hidden="true" />
                  </span>
                  <span>
                    <img src={twittericon} className="twitter" alt="" aria-hidden="true" />
                  </span>
                  <span>
                    <img src={instagramicon} className="insta" alt="" aria-hidden="true" />
                  </span>
                </div>
              </Col>

              <Col md={4} className="footer-info-blk rv-d1" data-reveal="up">
                <p>Platform</p>
                <ul>
                  {PLATFORM_LINKS.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </Col>

              <Col md={3} className="footer-info-blk rv-d2" data-reveal="up">
                <p>Resources</p>
                <ul>
                  {RESOURCE_LINKS.map((item) => (
                    <li key={item.to}>
                      <Link to={item.to}>{item.label}</Link>
                    </li>
                  ))}
                </ul>
              </Col>
            </Row>
            <div className="footer-copyryt-txt">
              <p>© {new Date().getFullYear()} Rainlink. All rights reserved.</p>
            </div>
          </div>
        </Container>
      </footer>
    </div>
  );
}

export default LandingpgComponent;
